import { captureException } from '@sentry/core';
import uniq from 'lodash/uniq';

import { PermissionType } from '@watershed/constants/permissions';

import { getObjectKeys } from '../getObjectKeys';

/**
 * This mapping is quite structural - it sets up the hierarchy of permissions,
 * such that each child gives implicit access to the permissions of its parent.
 * DO NOT delete from this list unless there are no more existing records in the database.
 *
 * i.e. WatershedAdmin also gives you Admin permissions, whereas Admin
 * permissions give you CorporateAdmin permissions.
 */
export const PERMISSION_PARENTS: {
  [key in PermissionType]?: Array<PermissionType>;
} = {
  // Admin magicsauce
  [PermissionType.Admin]: [PermissionType.WatershedAdmin],
  [PermissionType.CorporateAdmin]: [PermissionType.Admin],
  [PermissionType.ManageMarketplacePurchases]: [PermissionType.CorporateAdmin],
  [PermissionType.ManageCompanyTags]: [PermissionType.CorporateAdmin],
  [PermissionType.ManageOrgHierarchy]: [PermissionType.ManageMeasurement],
  [PermissionType.ViewEmployeeReport]: [PermissionType.CorporateAdmin],
  [PermissionType.ManageSingleSignOn]: [PermissionType.CorporateAdmin],
  [PermissionType.ManageMeasurement]: [PermissionType.CorporateAdmin],
  [PermissionType.ManageReductionPlans]: [PermissionType.CorporateAdmin],
  [PermissionType.ManageSuppliers]: [PermissionType.CorporateAdmin],
  [PermissionType.ManageDisclosures]: [PermissionType.CorporateAdmin],

  // Manage/View and other hierarchies
  [PermissionType.ViewFootprintDetail]: [
    PermissionType.ViewReductions,
    PermissionType.ManageMeasurement,
    // Note: This doesn't seem right but we don't want to hit permissions
    // landmines with Finance Reporting
    PermissionType.FinanceAdmin,
  ],
  [PermissionType.ViewAuditDetail]: [PermissionType.ViewFootprintDetail],
  [PermissionType.ViewReductions]: [PermissionType.ManageReductionPlans],
  [PermissionType.ManageDataset]: [PermissionType.ManageMeasurement],
  [PermissionType.ManageDatasource]: [PermissionType.ManageDataset],
  [PermissionType.ApproveDataset]: [PermissionType.ManageMeasurement],
  [PermissionType.ApproveDatasource]: [PermissionType.ApproveDataset],
  [PermissionType.EditReport]: [PermissionType.ViewFootprintDetail],
  [PermissionType.ViewReport]: [PermissionType.EditReport],
  [PermissionType.EditReportQuestionInstance]: [PermissionType.EditReport],
  [PermissionType.ViewReportQuestionInstance]: [
    PermissionType.ViewReport,
    PermissionType.EditReportQuestionInstance,
  ],

  // Specific footprint permissions
  [PermissionType.ViewFootprint]: [
    PermissionType.ApproveFootprint, // Approve grants View on the same footprint
    PermissionType.ViewFootprintDetail, // access to _all_ footprints if you have ViewFootprintDetail
    PermissionType.ManageSuppliers, // access to _all_ footprints if you have ManageSuppliers
  ],
  [PermissionType.ApproveFootprint]: [PermissionType.ManageMeasurement],

  // Calculation permissions
  [PermissionType.EditCalculation]: [PermissionType.Admin],
  [PermissionType.ViewCalculation]: [PermissionType.EditCalculation],

  // Finance
  [PermissionType.FinanceAdmin]: [PermissionType.Admin],
  [PermissionType.ManageFund]: [PermissionType.FinanceAdmin],
  [PermissionType.FinanceReadOnly]: [PermissionType.ManageFund],

  // Learning Hub
  // This is unique permission in that the learning hub is not actually gated by
  // any permission, but we want to allow orgs to create a user that can _exclusively_
  // access learning hub.

  // It's only part of the permission hierarchy so that admin users can grant it to others.
  [PermissionType.ViewLearningHub]: [
    PermissionType.CorporateAdmin,
    PermissionType.FinanceAdmin,
  ],
};

/**
 * Returns the hierarchy of permissions for a given permission.
 *
 * @param permission - The permission to get the hierarchy for
 * @returns An array of permissions that are ancestors of the given permission,
 * sorted by the most specific permissions first, broadest admin permissions last.
 */
function _getPermissionsHierarchy(
  permission: PermissionType
): Array<PermissionType> {
  const flattenedResult = [
    permission,
    ...(PERMISSION_PARENTS[permission] || []).map((parent) =>
      _getPermissionsHierarchy(parent)
    ),
  ].flat();
  // Ensures the most specific permissions are first, broadest admin permissions last
  return uniq(flattenedResult.reverse()).reverse();
}

/**
 * Memoized version of _getPermissionsHierarchy for all PermissionTypes.
 */
const ANCESTORS_FOR_PERMISSION = Object.fromEntries(
  getObjectKeys(PermissionType).map((permission) => [
    permission,
    _getPermissionsHierarchy(permission),
  ])
);

/**
 * Returns the hierarchy of permissions for a given permission. Uses pre-cached
 * values for performance.
 *
 * @param permission - The permission to get the hierarchy for
 * @returns An array of permissions that are ancestors of the given permission,
 * sorted by the most specific permissions first, broadest admin permissions last.
 */
export function getPermissionsHierarchy(
  permission: PermissionType
): Array<PermissionType> {
  return ANCESTORS_FOR_PERMISSION[permission];
}

const HIERARCHY_LENGTHS_FOR_PERMISSION = Object.fromEntries(
  getObjectKeys(PermissionType).map((permission) => [
    permission,
    getPermissionsHierarchy(permission).length,
  ])
);

export type SourceObjectWithPermissionDelegate = {
  id: string;
  permissionDelegateId?: string | null;
};

export interface PermissionOrigin {
  permission: string;
  objectId?: string | null;
  objectName?: string | null;
}

type PermissionChecker = (params: {
  permissionToCheck: {
    object?: {
      id: string;
      permissionDelegateId?: string | null;
    } | null;
  };
  currentPermission: {
    permission: string;
    objectId?: string | null;
  };
  allowAnyObject?: boolean;
}) => PermissionOrigin | null;

// A map of statically defined permission type to an optimized checker function
const PERMISSION_ORIGIN_CHECKERS = Object.fromEntries(
  getObjectKeys(PermissionType).map((permission) => {
    // Fetch array of parent permissions, any of which give access to this permission
    const parentPermissionsToCheck = getPermissionsHierarchy(permission);

    /**
     * Checks if the current permission is a parent of the permission to check,
     * handling object ids if provided.
     *
     * @param permissionToCheck - contains the object id of the permission to check
     * @param currentPermission - the permission the user has, including permission type and object id
     * @param allowAnyObject - if true, we don't check the object ID
     * @returns the explicitly granted permission that grants access to the permissionToCheck
     */
    const checker: PermissionChecker = ({
      permissionToCheck,
      currentPermission,
      allowAnyObject,
    }) => {
      const { permission, objectId: currentObjectId } = currentPermission;
      const { object: objectToCheck } = permissionToCheck;
      // Iterate through the permission hierarchy
      for (const parentPermissionToCheck of parentPermissionsToCheck) {
        if (parentPermissionToCheck === permission) {
          if (!currentObjectId) {
            // Has access to all objects for this permission, e.g. manage all datasets.
            // Or this is an org-level (non-object-specific) permission.
            return { permission: parentPermissionToCheck };
          }
          if (allowAnyObject) {
            // Has access to some or all objects for this permission, and we don't care which one
            return { permission: parentPermissionToCheck };
          }
          if (
            currentObjectId === objectToCheck?.id ||
            currentObjectId === objectToCheck?.permissionDelegateId
          ) {
            // Has access to this specific object
            return {
              permission: parentPermissionToCheck,
              objectId: currentObjectId,
            };
          }
        }
      }
      // No permissions found that give them access to target permission
      return null;
    };

    return [permission, checker];
  })
);

/**
 * Check if the current permission grants access to the permissionToCheck by traversing the hierarchy
 * and checking object ids if provided.
 *
 * @param currentPermission - the permission the user has
 * @param permissionToCheck - the permission being checked
 * @param allowAnyObject - if true, we don't check the object ID
 * @returns some variant of currentPermission plus/minus an object id if the current permission grants
 * access to the permission to check, or null if it doesn't grant access
 */
export function getPermissionOrigin({
  currentPermission,
  permissionToCheck,
  allowAnyObject,
}: {
  currentPermission: {
    permission: PermissionType;
    objectId?: string | null;
  };
  permissionToCheck: {
    permission: PermissionType;
    object?: {
      id: string;
      // An object can delegate permission to another object, e.g. file metadata can delegate permissions to a datasource
      permissionDelegateId?: string | null;
    } | null;
  };
  allowAnyObject?: boolean;
}): PermissionOrigin | null {
  const checker = PERMISSION_ORIGIN_CHECKERS[permissionToCheck.permission];
  return checker({
    permissionToCheck,
    currentPermission,
    allowAnyObject,
  });
}

/**
 * Sorts permissions so that the one with the shortest hierarchy is first. If
 * two permissions have the same hierarchy length, then the one without an
 * objectId is prioritized.
 *
 * @param permissions an array of permissions to sort
 * @returns a new array of permissions sorted by the shortest hierarchy
 */
export function sortPermissionsByShortestHierarchy<
  T extends {
    permission: PermissionType;
    objectId?: string | null;
  },
>(permissions: Array<T>): Array<T> {
  try {
    return permissions.toSorted((a, b) => {
      const aLength =
        HIERARCHY_LENGTHS_FOR_PERMISSION[a.permission] + (!!a.objectId ? 1 : 0);
      const bLength =
        HIERARCHY_LENGTHS_FOR_PERMISSION[b.permission] + (!!b.objectId ? 1 : 0);

      return aLength - bLength;
    });
  } catch (e) {
    captureException(e, {
      tags: {
        toSorted: typeof [].toSorted,
        permissions: JSON.stringify(permissions),
      },
    });
    if (e instanceof TypeError) {
      console.info({
        toSorted: typeof [].toSorted,
        permissions: JSON.stringify(permissions),
      });
      if (typeof [].toSorted !== 'function') {
        console.warn(
          'toSorted is not supported in this browser, falling back to sort'
        );
      }
      // Fallback to a more compatible sorting method
      return permissions.sort((a, b) => {
        const aLength =
          HIERARCHY_LENGTHS_FOR_PERMISSION[a.permission] +
          (!!a.objectId ? 1 : 0);
        const bLength =
          HIERARCHY_LENGTHS_FOR_PERMISSION[b.permission] +
          (!!b.objectId ? 1 : 0);

        return aLength - bLength;
      });
    }
    return permissions;
  }
}

/**
 * Returns the explicitly granted permission that grants access to the permissionToCheck,
 * using the shortest hierarchy (~most powerful permission). For example, if the user has Admin and Manage Measurement
 * and we're checking a dataset, return Admin.
 *
 * @param currentPermissions - the permissions the user or role has
 * @param permissionToCheck - the permission being checked
 * @returns the permission currently granted that a) gives access to permission to check
 * and b) has the shortest hierarchy, or null if no permission gives access to the permission to check
 */
export function getShortestHierarchyPermissionOrigin({
  currentPermissions,
  permissionToCheck,
}: {
  // Permissions that a user or a role has
  currentPermissions: Array<{
    permission: PermissionType;
    objectId?: string | null;
  }>;
  permissionToCheck: {
    permission: PermissionType;
    objectId?: string | null;
  };
}): PermissionOrigin | null {
  for (const p of sortPermissionsByShortestHierarchy(currentPermissions)) {
    const origin = getPermissionOrigin({
      currentPermission: p,
      permissionToCheck: {
        permission: permissionToCheck.permission,
        object: permissionToCheck.objectId
          ? { id: permissionToCheck.objectId }
          : undefined,
      },
    });
    if (origin) return origin;
  }
  return null;
}
